import {
    Field,
    Formik,
} from 'formik';
import * as Yup from 'yup';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Container,
    Stack,
    Text,
} from '@mantine/core';
import {
    FormikPasswordInput,
    FormikSwitch,
    FormikTextInput,
    FormWithButtons,
} from '@flexinet/ui-components';

import {RevalidateFormValues} from './types';
import {ProviderConnection} from '../types';

// FNP-1248 — THE RE-VALIDATE FORM. How a tenant fixes a connection after
// editing their trust policy.
//
// ⚠️ NO PROVIDER PICKER AND NO DISPLAY NAME. `RotateProviderCredentialInput`
// has neither, deliberately: the provider is a property of the connection being
// rotated, and accepting one would let a caller move a connection between
// providers under the guise of a credential change. The credential type is read
// off the connection, not chosen.
//
// ⚠️ THE ROLE ARN IS PREFILLED, THE TOKEN IS NEVER PREFILLED.
//
// A role ARN is an identifier the tenant already gave us and can see on the
// detail page; prefilling it means the common case — "I fixed my trust policy,
// re-check the same role" — is one click. A token CANNOT be prefilled: no
// output type carries it, there is nothing to prefill FROM, and the tenant must
// paste a fresh one. The asymmetry is the schema's, not a UI choice.

const revalidateSchema = (credentialType: string) => Yup.object()
                                                       .shape({
                                                           roleArn: credentialType === 'ASSUMED_ROLE'
                                                               ? Yup.string()
                                                                    .required('Enter the ARN of the role in your AWS account')
                                                                    .matches(
                                                                        /^arn:[a-z0-9-]+:iam::\d{12}:role\/.+$/,
                                                                        'Enter a role ARN, for example arn:aws:iam::123456789012:role/FlexiDnsAccess',
                                                                    )
                                                               : Yup.string()
                                                                    .strip(),
                                                           apiToken: credentialType === 'API_TOKEN'
                                                               ? Yup.string()
                                                                    .required('Enter the API token issued by your provider')
                                                               : Yup.string()
                                                                    .strip(),
                                                           writable: Yup.boolean(),
                                                       });

export const RevalidateForm = (
    {
        connection,
        roleArn,
        handleSubmit,
        handleCancel,
    }: {
        connection: ProviderConnection,
        // The role ARN to prefill, when the caller knows it. Not stored on the
        // connection record, so it is legitimately absent on a fresh page load
        // and the tenant re-enters it.
        roleArn?: string,
        handleSubmit: (values: RevalidateFormValues) => Promise<unknown>,
        handleCancel?: () => void,
    },
) => {

    const isAssumedRole = connection.credentialType === 'ASSUMED_ROLE';

    // Wrapped rather than passed through: FormWithButtons types its cancel prop
    // as a MouseEventHandler, which is not optional.
    const onCancel = () => {
        handleCancel?.();
    };

    const initialValues: RevalidateFormValues = {
        credentialType: connection.credentialType,
        roleArn: roleArn ?? '',
        apiToken: '',
        // ⚠️ SEEDED FROM THE CONNECTION'S CURRENT DECLARATION, not hard-false.
        //
        // FNP-1233's default-off rule governs CREATION. On a rotation, resetting
        // an already-writable connection to read-only because the tenant did not
        // re-tick the box would silently REVOKE a capability they never asked to
        // give up. `writable === true` is still the only thing that reads as on,
        // so an undeployed/absent field still lands false.
        writable: connection.writable === true,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={revalidateSchema(connection.credentialType)}
            onSubmit={(values: RevalidateFormValues) => handleSubmit(values)}
        >
            {({
                  handleSubmit: formikHandleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons
                    handleSubmit={formikHandleSubmit}
                    isSubmitting={isSubmitting}
                    handleCancel={onCancel}
                    // Hard-wired off: the values object holds `apiToken`, and
                    // DisplayFormikState would render it on screen.
                    debug={false}
                    {...rest}
                >
                    <Container>
                        <Stack gap={'md'}>

                            {isAssumedRole && (
                                <Alert color={'blue'} variant={'light'}>
                                    <Text size={'sm'}>
                                        {I18n.get('Your external ID does not change when you re-validate, so the value already in your trust policy stays correct. Submit the same role ARN once the trust policy is updated.')}
                                    </Text>
                                </Alert>
                            )}

                            {isAssumedRole ? (
                                <Field
                                    name={'roleArn'}
                                    label={I18n.get('Role ARN')}
                                    description={I18n.get('The IAM role in your AWS account that Flexi will assume.')}
                                    component={FormikTextInput}
                                    withAsterisk
                                />
                            ) : (
                                <Field
                                    name={'apiToken'}
                                    label={I18n.get('New API token')}
                                    description={I18n.get('Paste a fresh token. Flexi cannot show you the existing one — it is stored encrypted and is never readable back.')}
                                    component={FormikPasswordInput}
                                    withAsterisk
                                />
                            )}

                            {/* FNP-1233 — the same declaration, re-affirmable.
                                This is the promotion path: a connection becomes
                                writable when the tenant declares the credential
                                can write, after fixing their trust policy. */}
                            <Field
                                name={'writable'}
                                label={isAssumedRole
                                    ? I18n.get('This role can create and delete zones')
                                    : I18n.get('This token can create and delete zones')}
                                description={I18n.get('Leave this off if you granted read-only access. Flexi will not attempt any write through this connection unless you turn it on.')}
                                component={FormikSwitch}
                            />

                        </Stack>
                    </Container>
                </FormWithButtons>
            )}
        </Formik>
    );
};
